import { useEffect, useState, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import useAuth from '../../../hooks/useAuth';
import { getStepById } from '../utils/getStepById';
import useAxiosPrivate from '../../../hooks/useAxiosPrivate';

import LoadingSpinner from '../../loadingSpinner/LoadingSpinner';
import Error from '../Error';
import VideoPlayer from './StepPageVideoPlayer';

import { useTranslation } from 'react-i18next';
import { Trans } from 'react-i18next';

import '../../../css/StepPage.css'

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTrash } from '@fortawesome/free-solid-svg-icons';

export default function StepPage({ isNavOpen, customRoles }) {
  const { step_id } = useParams();
  const { auth } = useAuth();
  const IS_DEMO_MODE = process.env.REACT_APP_DEMO_MODE === 'true';

  const maxFileSize =
    Number(process.env.REACT_APP_MAX_FILE_SIZE) || 300;

  const isAdmin = auth?.roles?.includes('Admin');
  const navigate = useNavigate();
  const axiosPrivate = useAxiosPrivate();
  const { t } = useTranslation();

  const [step, setStep] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [nonCriticalError, setNonCriticalError] = useState('');
  const [videoUrl, setVideoUrl] = useState(null);
  const [audioUrl, setAudioUrl] = useState(false);

  const [showConfirm, setShowConfirm] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);

  const [isEditing, setIsEditing] = useState(false);
  const [editedName, setEditedName] = useState('');
  const [editedDescription, setEditedDescription] = useState('');
  const [selectedRoles, setSelectedRoles] = useState([]);
  const [newVideo, setNewVideo] = useState(null);
  const [newAudio, setNewAudio] = useState(null);

  const [hasVideo, setHasVideo] = useState(false);
  const [videoStatus, setVideoStatus] = useState('checking');
  const hasCheckedVideoRef = useRef(false);

  const videoRef = useRef(null);
  const audioRef = useRef(null);

  const [videoUploadProgress, setVideoUploadProgress] = useState(0);
  const [videoProcessing, setVideoProcessing] = useState(false);
  const [audioUploadProgress, setAudioUploadProgress] = useState(0);

  const [isUploading, setIsUploading] = useState(false);
  const [messages, setMessages] = useState([]);

  const videoInputRef = useRef(null);
  const audioInputRef = useRef(null);


  // 🎙 Audio recording (edit mode)
  const [isRecording, setIsRecording] = useState(false);
  const [recordedAudioURL, setRecordedAudioURL] = useState(null);

  const mediaRecorderRef = useRef(null);
  const audioChunksRef = useRef([]);

  const [isAudioLoading, setIsAudioLoading] = useState(false);
  const [showVideoHint, setShowVideoHint] = useState(false);
  const [showAudioHint, setShowAudioHint] = useState(false);

  const BACKEND = process.env.REACT_APP_BACKEND_URL;

  const [isFavorite, setIsFavorite] = useState(false)
  // console.log('isFavorite', isFavorite)

  // message helpers (same as StepAdd)
  const addMessage = (text, { temporary = false } = {}) => {
    const id = Date.now() + Math.random();
    setMessages(prev => [...prev, { id, text, temporary }]);
    return id;
  };

  const removeMessage = (id) => {
    setMessages(prev => prev.filter(msg => msg.id !== id));
  };

  const handleDeleteStep = async () => {
    setIsDeleting(true);

    try {
      await axiosPrivate.delete(`/steps/${step.step_id}`, {
        data: { userId: auth.userId },
      });

      // Success → go back
      navigate(-1);

    } catch (err) {
      console.error(t('stepPage.errorDeletingStep'), err);

      const status = err?.response?.status;
      const backendMessage =
        err?.response?.data?.error || t('stepPage.failedToDeleteStep');

      // 👉 NON-CRITICAL (demo mode)
      if (status === 403) {
        setNonCriticalError(backendMessage);
        return;
      }

      // 👉 CRITICAL
      setError(backendMessage); // or keep alert if you must
    } finally {
      setIsDeleting(false);
      setShowConfirm(false);
    }
  };


  const startAudioRecording = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });

      const mediaRecorder = new MediaRecorder(stream);
      mediaRecorderRef.current = mediaRecorder;
      audioChunksRef.current = [];

      mediaRecorder.ondataavailable = (e) => {
        if (e.data.size > 0) {
          audioChunksRef.current.push(e.data);
        }
      };

      mediaRecorder.onstop = () => {
        const blob = new Blob(audioChunksRef.current, {
          type: mediaRecorder.mimeType || 'audio/webm',
        });

        if (blob.size < 1000) {
          addMessage(t('addStep.emptyRecording'));
          setIsRecording(false);
          return;
        }

        const fileExtension = blob.type.includes('mp4') ? 'mp4' : 'webm';

        const file = new File(
          [blob],
          `recording_${Date.now()}.${fileExtension}`,
          { type: blob.type }
        );

        // ✅ StepPage uses setNewAudio
        setNewAudio(file);
        setRecordedAudioURL(URL.createObjectURL(blob));

        // ✅ Release mic (Safari-safe)
        mediaRecorder.stream
          ?.getTracks()
          .forEach(track => track.stop());
      };



      mediaRecorder.start(1000); // ⏱️ force dataavailable every 1s (Safari fix)
      setIsRecording(true);

      // ⏱ Auto-stop after 30s
      setTimeout(() => {
        if (mediaRecorder.state === 'recording') {
          mediaRecorder.requestData(); // ✅ Safari flush
          mediaRecorder.stop();
          setIsRecording(false);
        }

      }, 30_000);

    } catch (err) {
      console.error('Microphone access denied:', err);
      addMessage(t('addStep.microphonePermissionDenied'));
    }
  };

  const stopAudioRecording = () => {
    const recorder = mediaRecorderRef.current;
    if (!recorder) return;

    // 🧠 Safari needs this to flush audio
    if (recorder.state === 'recording') {
      recorder.requestData(); // ✅ CRITICAL for iOS
      recorder.stop();
    }

    setIsRecording(false);
  };


  const clearRecordedAudio = () => {
    setNewAudio(null);
    setRecordedAudioURL(null);
  };


  const handleUpdateStep = async () => {

    // 🚫 Prevent saving if no role selected
    if (selectedRoles.length === 0) {
      addMessage(
        t('stepPage.mustAssignRole'));
      return;
    }

    setIsUploading(true);
    setMessages([]);

    try {
      // 1️⃣ Send update for text
      const res = await axiosPrivate.patch(`/steps/${step.step_id}`, {
        userId: auth?.userId,
        step_name: editedName,
        step_description: editedDescription,
        videoUpdated: !!newVideo,
        audioUpdated: !!newAudio,
        role_ids: selectedRoles,
      });

      const { data: updatedStepData } = await axiosPrivate.post(`/steps/${step.step_id}`, {
        userId: auth.userId
      });
      const updatedSlug = updatedStepData.step_slug;

      if (res.status === 200) {
        addMessage(t('stepPage.stepUpdated'));
      }

      // 2️⃣ Upload media if any
      if (newVideo) {
        const formData = new FormData();
        formData.append('video', newVideo);
        formData.append('userId', auth.userId);

        setVideoUploadProgress(0);
        setVideoProcessing(false);

        // 👇 create initial temporary message
        let msgId = addMessage(
          t('addStep.uploadingVideoProgress', { percent: 0 }),
          { temporary: true }
        );

        try {
          await axiosPrivate.post(
            `/videos/upload/${step.step_id}/${updatedSlug}`,
            formData,
            {
              headers: { 'Content-Type': 'multipart/form-data' },
              timeout: 0,
              onUploadProgress: (e) => {
                const percent = Math.round((e.loaded * 100) / e.total);
                setVideoUploadProgress(percent);

                // replace temp message
                removeMessage(msgId);
                msgId = addMessage(
                  t('addStep.uploadingVideoProgress', { percent }),
                  { temporary: true }
                );

                if (percent === 100) {
                  setVideoProcessing(true);
                }
              },
            }
          );

          // ✅ success
          removeMessage(msgId);
          setVideoUploadProgress(0);
          setVideoProcessing(false);
          addMessage(t('stepPage.videoUpdatedSuccess'));

        } catch (err) {
          removeMessage(msgId);
          setVideoUploadProgress(0);
          setVideoProcessing(false);

          const status = err.response?.status;
          const errorText =
            err.response?.data?.error ||
            err.response?.data?.message ||
            '';

          if (status === 413 || errorText.includes('File is too large')) {
            addMessage(
              t('addStep.fileTooLarge', { maxSize: maxFileSize })
            );
            setIsUploading(false);
            return;
          }

          if (status === 403 && errorText === 'STORAGE_LIMIT_REACHED') {
            addMessage(t('stepPage.storageLimitReached'));
            setIsUploading(false);
            return;
          }

          addMessage(
            `${t('stepPage.errorUpdatingVideo')} ${errorText || 'Upload failed'}`
          );
          setIsUploading(false);
          return;
        }
      }


      if (newAudio) {
        const formData = new FormData();
        formData.append('audio', newAudio);
        formData.append('userId', auth.userId); // ✅ add same consistency

        const msgId = addMessage(t('stepPage.uploadingAudio'), { temporary: true });
        try {
          await axiosPrivate.post(`/audios/upload/${step.step_id}/${updatedSlug}`, formData, {
            headers: { 'Content-Type': 'multipart/form-data' },
          });
          removeMessage(msgId);
          addMessage(t('stepPage.audioUpdatedSuccess'));
        } catch (err) {
          removeMessage(msgId);
          const msg = err.response?.data?.message || err.message;
          addMessage(`${t('stepPage.errorUpdatingAudio')} ${msg}`);
          console.error('Audio upload failed:', msg);

          // ❌ Stop here – don’t continue with refresh or closing edit mode
          setIsUploading(false);
          return;
        }
      }

      // 3️⃣ Reset form state
      setNewVideo(null);
      setNewAudio(null);
      if (videoInputRef.current) videoInputRef.current.value = '';
      if (audioInputRef.current) audioInputRef.current.value = '';

      // ✅ Keep edit mode open until after messages show
      // comment this line out for now to observe messages
      // setIsEditing(false);

      addMessage(t('stepPage.stepUpdated'));


      // Now refresh and close edit mode
      const updatedData = await getStepById(step.step_id, auth.userId);
      setStep(updatedData);
      setIsEditing(false);
      setIsUploading(false);



      // 4️⃣ Refresh step data visually
      // const updatedData = await getStepById(step.step_id, auth.userId);
      // setStep(updatedData);
    } catch (err) {
      console.error('❌ Error updating step:', err);

      const backendError = err.response?.data?.error;
      const backendMsg = err.response?.data?.message || err.message;

      // 🎯 Specific known backend error: duplicate step name
      if (backendError === 'STEP_EXISTS') {
        addMessage(t('stepPage.stepExistsError'));
      }
      // 🎯 Storage-related error
      else if (backendError === 'STORAGE_LIMIT_REACHED') {
        addMessage(backendMsg);
      }
      // ❌ Generic fallback
      else {
        addMessage(`❌ ${backendMsg || t('stepPage.errorUpdatingStep')}`);
      }

      setIsUploading(false);
      return;
    }

  };
  const toggleFavorite = async (step_id) => {
    if (!step_id) return;

    try {
      if (isFavorite) {
        setIsFavorite(false)
        // console.log("Logic to unfavorite")
        await axiosPrivate.delete(`/steps/${step_id}/favorite`, {
          data: { userId: auth.userId },
        });
      } else {
        setIsFavorite(true)
        // console.log("Logic to favorite")
        await axiosPrivate.post(`/steps/${step_id}/favorite`, {
          userId: auth.userId,
        });
      }

    } catch (err) {
      console.error("❌ Failed to toggle favorite:", err);
    }


  }

  const handleNewVideoChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    const maxBytes = maxFileSize * 1024 * 1024;

    if (file.size > maxBytes) {
      addMessage(
        t('addStep.fileTooLarge', { maxSize: maxFileSize })
      );

      e.target.value = null;
      setNewVideo(null);
      return;
    }

    // Clear previous size errors
    setMessages(prev =>
      prev.filter(
        m => !m.text?.includes(t('addStep.fileTooLarge', { maxSize: maxFileSize }))
      )
    );

    setNewVideo(file);
  };



  useEffect(() => {
    if (step) {
      setEditedName(step.step_name);
      setEditedDescription(step.step_description || '');
    }
  }, [step]);

  // ✅ Fetch roles assigned to this step
  useEffect(() => {
    const fetchStepRoles = async () => {
      const res = await axiosPrivate.post(`/steps/${step.step_id}`, { userId: auth.userId });
      if (res.data.role_ids)
        setSelectedRoles(res.data.role_ids.map(id => parseInt(id))); // ✅ normalize
    };
    if (step) fetchStepRoles();
  }, [step]);


  useEffect(() => {
    if (step) {
      setEditedName(step.step_name);
      setEditedDescription(step.step_description || '');
    }
  }, [step]);

  useEffect(() => {
    const fetchStep = async () => {
      if (!step_id || !auth?.userId) return;

      setIsLoading(true);
      setError(null);

      try {
        const data = await getStepById(step_id, auth.userId);

        setStep(data);
        setHasVideo(Boolean(data.has_video));


      } catch (err) {
        console.error(t('stepPage.errorFetchingStep'), err);
        setError(err.message || t('stepPage.errorFetchingStep'));
      } finally {
        setIsLoading(false);
      }
    };

    fetchStep();
  }, [step_id, auth?.userId]);

  useEffect(() => {
    if (!step || !auth?.accessToken) return;

    let cancelled = false;

    const fetchVideo = async () => {

      try {
        // 🔹 STEP 1: HEAD — check existence ONLY
        const headResponse = await fetch(
          `${BACKEND}/media/videos/${step.step_id}/${step.step_slug}.mp4`,
          {
            method: 'HEAD',
            headers: { Authorization: `Bearer ${auth.accessToken}` },
          }
        );

        if (cancelled) return;

        hasCheckedVideoRef.current = true;

        // 🔴 Still encoding
        if (headResponse.status === 404 || headResponse.status === 202) {
          setVideoStatus('encoding');
          return;
        }

        if (!headResponse.ok) throw new Error('HEAD_FAILED');

        // 🔹 STEP 2: NOW we know it exists → show loading
        setVideoStatus('loading');

        const response = await fetch(
          `${BACKEND}/media/videos/${step.step_id}/${step.step_slug}.mp4?cacheBust=${Date.now()}`,
          { headers: { Authorization: `Bearer ${auth.accessToken}` } }
        );

        if (cancelled) return;
        if (!response.ok) throw new Error('FETCH_FAILED');

        // 🔹 STEP 3: Validate + prepare video
        const blob = await response.blob();
        if (!blob || blob.size < 10_000) throw new Error('TOO_SMALL');

        const url = URL.createObjectURL(blob);

        await new Promise((resolve, reject) => {
          const probe = document.createElement('video');
          probe.preload = 'metadata';
          probe.onloadedmetadata = resolve;
          probe.onerror = reject;
          probe.src = url;
        });

        if (cancelled) return;

        setVideoUrl(url);
        setVideoStatus('ready');
      } catch (err) {
        if (!cancelled) {
          hasCheckedVideoRef.current = true;
          setVideoStatus('encoding');
        }
      }
    };



    fetchVideo();
    return () => {
      cancelled = true;
    };
  }, [step, auth?.accessToken]);



  useEffect(() => {
    if (!step || !auth?.accessToken) return;

    let hintTimer;

    const fetchAudio = async () => {
      setIsAudioLoading(true);
      setShowAudioHint(false);

      hintTimer = setTimeout(() => {
        setShowAudioHint(true);
      }, 1000); // ⏱ 1 second

      try {
        const response = await fetch(
          `${BACKEND}/media/audios/${step.step_id}/${step.step_slug}.mp3?cacheBust=${Date.now()}`,
          {
            headers: { Authorization: `Bearer ${auth.accessToken}` },
          }
        );

        if (!response.ok) throw new Error(t('stepPage.audioNotAvailable'));

        const blob = await response.blob();
        const url = URL.createObjectURL(blob);
        setAudioUrl(url);
      } catch (err) {
        setAudioUrl(null);
      } finally {
        clearTimeout(hintTimer);
        setIsAudioLoading(false);
      }
    };


    fetchAudio();
    return () => clearTimeout(hintTimer);
  }, [step, auth?.accessToken]);

  useEffect(() => {

    const fetchIsFavorite = async () => {

      if (!step || !auth?.accessToken) return;

      try {

        const res = await axiosPrivate.get(`steps/${step_id}/favorite`);

        setIsFavorite(res.data.isFavorite)
      } catch (err) {
        console.error("❌ Failed to fetch isFavorite:", err);
      }
    };
    fetchIsFavorite()

  }, [step])



  if (isLoading) return <LoadingSpinner />;
  if (error) return <Error error={error} isNavOpen={isNavOpen} />;
  if (!step) return <p>{t('stepPage.stepNotFound')}</p>;


  return (
    <div className={`${isNavOpen ? 'body-squeezed' : 'body'}`}>
      <button className="button-white" onClick={() => navigate(-1)}>
        {t('stepPage.back')}
      </button>

      {!isEditing &&
        <div className="step-page-card step-card">

          <button
            className="star-button"
            onClick={(e) => {
              e.stopPropagation(); // prevent navigation
              toggleFavorite(step.step_id);
            }}
            // disabled={isToggling}
            title={
              isFavorite
                ? t("rolePage.removeFromFavorites", "Remove from favorites")
                : t("rolePage.addToFavorites", "Add to favorites")
            }
          >
            {isFavorite ? "★" : "☆"}
          </button>

          <h2>{step.step_name}</h2>
          {step.step_description && <p>{step.step_description}</p>}

          {/* Audio Player */}
          {isAudioLoading && (
            <p className="media-hint">
              {t('stepPage.loadingAudio', 'Loading audio…')}
            </p>
          )}

          {!isAudioLoading && audioUrl && (
            <audio controls style={{ width: '100%', maxWidth: '300px', marginBottom: '1rem' }}>
              <source src={audioUrl} type="audio/mpeg" />
              {t('stepPage.audioNotSupported')}
            </audio>
          )}

          {/* {!isAudioLoading && !audioUrl && (
            <p className="media-hint">
              {t('stepPage.noAudio', 'No audio available for this step')}
            </p>
          )} */}

          {/* 🎥 Video section */}

          {hasVideo && hasCheckedVideoRef.current && videoStatus === 'encoding' && (
            <p className="media-hint">
              {t('addStep.videoSuccessDelayed')}
            </p>
          )}

          {videoStatus === 'loading' && (
            <p className="media-hint">
              {t('stepPage.loadingVideo', 'Loading video…')}
            </p>
          )}

          {videoStatus === 'ready' && (
            <VideoPlayer videoUrl={videoUrl} />
          )}



          {/* {!isVideoLoading && !videoUrl && (
            <p className="media-hint">
              {t('stepPage.noVideo', 'No video available for this step')}
            </p>
          )} */}


        </div>
      }

      {isAdmin && (
        <>

          {isEditing ? (
            <div className="edit-form">
              <label>
                {t('stepPage.labelStepName')}
                <input
                  type="text"
                  value={editedName}
                  onChange={(e) => setEditedName(e.target.value)}
                  disabled={isUploading || isDeleting}
                />
              </label>

              <label>
                {t('stepPage.labelDescription')}
                <textarea
                  rows="4"
                  value={editedDescription}
                  onChange={(e) => setEditedDescription(e.target.value)}
                  disabled={isUploading || isDeleting}
                />
              </label>

              {/* ✅ Roles (levels) */}
              <label>{t('stepPage.labelRoles')}</label>
              <div style={{ marginBottom: '1rem' }}>
                {customRoles?.map(role => (
                  <label key={role.role_id} style={{ display: 'block' }}>
                    <input
                      type="checkbox"
                      value={role.role_id}
                      checked={selectedRoles.includes(role.role_id)}
                      onChange={e => {
                        const id = parseInt(e.target.value);
                        if (e.target.checked) {
                          setSelectedRoles([...selectedRoles, id]);
                        } else {
                          setSelectedRoles(selectedRoles.filter(r => r !== id));
                        }
                      }}
                      disabled={isUploading || isDeleting}
                    />
                    {role.role_name}
                  </label>
                ))}
              </div>

              <label>
                {t('stepPage.replaceVideo')}
                <input
                  ref={videoInputRef}
                  type="file"
                  accept="video/*"
                  onChange={handleNewVideoChange}
                  disabled={isUploading || isDeleting}
                />

                {newVideo && (
                  <div
                    type="button"
                    onClick={() => {
                      setNewVideo(null);
                      if (videoInputRef.current) videoInputRef.current.value = '';
                    }}
                    className="remove-file"
                  >
                    ×
                  </div>
                )}
              </label>

              <label>{t('stepPage.replaceAudio')}</label>

              {/* 📁 Upload audio */}
              <input
                ref={audioInputRef}
                type="file"
                accept="audio/*"
                onChange={(e) => setNewAudio(e.target.files[0])}
                disabled={isUploading || isDeleting || isRecording}
              />

              {/* 🎙 Record audio */}
              <div style={{ marginTop: '0.5rem' }}>
                {!isRecording ? (
                  <button
                    type="button"
                    className="button-white"
                    onClick={startAudioRecording}
                    disabled={isUploading || isDeleting}
                  >
                    🎙 {t('addStep.recordAudio')}
                  </button>
                ) : (
                  <button
                    type="button"
                    className="button-white"
                    onClick={stopAudioRecording}
                  >
                    ⏹ {t('addStep.stopRecording')}
                  </button>
                )}
              </div>

              {/* ▶️ Preview */}
              {recordedAudioURL && (
                <div style={{ marginTop: '0.5rem' }}>
                  <audio controls src={recordedAudioURL} />
                </div>
              )}

              {/* ❌ Clear */}
              {newAudio && (
                <button
                  type="button"
                  onClick={() => {
                    clearRecordedAudio();
                    if (audioInputRef.current) audioInputRef.current.value = '';
                  }}
                  className="remove-file"
                >
                  ×
                </button>
              )}


              <div style={{ marginTop: '1rem', display: 'flex', justifyContent: 'center' }}>
                <button
                  onClick={handleUpdateStep}
                  className="button-white"
                  disabled={isUploading || isDeleting}
                  style={{ marginRight: '1em' }}
                >
                  {isUploading ? t('stepPage.processing') : t('stepPage.saveChanges')}
                </button>
                <button
                  onClick={() => setIsEditing(false)}
                  className="button-red"
                  disabled={isUploading || isDeleting}
                >
                  {t('stepPage.cancel')}
                </button>
              </div>

              {newVideo && videoUploadProgress > 0 && (
                <div style={{ marginBottom: '1rem' }}>
                  <progress value={videoUploadProgress} max="100" style={{ width: '100%' }} />
                  <small>
                    {videoUploadProgress < 100
                      ? t('addStep.uploadingVideoProgress', {
                        percent: videoUploadProgress
                      })
                      : t('addStep.videoProcessingNotice')}
                  </small>
                </div>
              )}

              {messages.length > 0 && (
                <div className="messages-box">
                  {messages.map((msg) => (
                    <p key={msg.id}>{msg.text}</p>
                  ))}
                </div>
              )}
            </div>
          ) : (
            <button
              className="button-white"
              style={{ minWidth: '200px', marginBottom: '2em' }}
              onClick={() => {
                if (IS_DEMO_MODE) {
                  setNonCriticalError(
                    t('stepPage.demoEditDisabled', 'For test purposes this step cannot be edited.')
                  );

                  setTimeout(() => setNonCriticalError(''), 4000);

                  return;
                }

                setIsEditing(true);
                setMessages([]);
              }}
              disabled={isUploading || isDeleting}
            >
              {t('stepPage.editStep')}
            </button>

          )}
        </>
      )}
      {!showConfirm && isAdmin &&
        <button className="button-red"
          style={{ minWidth: '200px' }}
          onClick={() => setShowConfirm(true)}
          disabled={isUploading || isDeleting}
        >
          <FontAwesomeIcon icon={faTrash} /> {t('stepPage.deleteStep')}

        </button>
      }


      {showConfirm && (
        <div className="confirm-box">
          <p>  <Trans i18nKey="stepPage.confirmDeleteStep" /></p>
          <div className="confirm-buttons">
            <button
              className="button-red"
              onClick={handleDeleteStep}
              disabled={isUploading || isDeleting}
            >
              {isDeleting ? t('stepPage.deleting') : t('stepPage.yesDelete')}
            </button>
            <button
              className="button-white"
              onClick={() => setShowConfirm(false)}
              disabled={isDeleting || isUploading}
            >
              {t('stepPage.cancel')}
            </button>
          </div>
        </div>
      )}


      {nonCriticalError && (
        <div className="non-critical-error">
          {nonCriticalError}
        </div>
      )}




    </div>
  );
}

